/* eslint consistent-return: 0 */

import { clone, includes, keys, orderBy } from "lodash";

import { getBopisStoreInventoryUrl, getStoresLocationUrl } from "../atg";
import Util from "../../lib/util";
import { getDistance } from "../../lib/geolocation";

const util = new Util("services/stores");

export const fetchStoreInventoryLegacy = async (ids, country, sku) => {
  try {
    const queryParams = `country=${country}&skus=${sku}&storeIDs=${ids}`;
    const url = getBopisStoreInventoryUrl(queryParams);
    const response = await util.api({ url });

    return response.data;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #fetchStoreInventory Error: ${e}`);
  }
};

export const getFormattedPhone = (digits) => {
  return digits?.replace?.(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3") ?? "";
};

export const getStoreHours = (data) => {
  try {
    return Object.entries(data)
      .filter((item) => item[0].includes("hours"))
      .reduce((acc, curr) => {
        if (curr) {
          const [key, val] = curr;
          acc[key] = val;
        }

        return acc;
      }, {});
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #getStoreHours Error: ${e}`);
  }
};

export const getCombinedHoursByDay = (data) => {
  try {
    const hoursObjects = [
      { day: "sunday" },
      { day: "monday" },
      { day: "tuesday" },
      { day: "wednesday" },
      { day: "thursday" },
      { day: "friday" },
      { day: "saturday" },
    ];

    for (const key of Object.keys(data)) {
      if (data.hasOwnProperty(key)) {
        for (const item in hoursObjects) {
          if (hoursObjects[item].day === key) {
            hoursObjects[item].hours = `${data[key].open}-${data[key].close}`;
            break;
          }
        }
      }
    }

    hoursObjects.forEach((item) => {
      if (item.hours === undefined) {
        item.hours = "Closed";
      }
    });

    return hoursObjects;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #getCombinedHoursByDay Error: ${e}`);
  }
};

export const getCombinedDays = (dayObjects) => {
  try {
    const combined = [];
    let index = -1;

    for (let i = 0; i < dayObjects.length; i += 1) {
      const day = dayObjects[i];

      if (index === -1 || combined[index].hours !== day.hours) {
        combined.push({ days: [day.day], hours: day.hours });
        index += 1;
      } else {
        combined[index].days.push(day.day);
      }
    }

    return combined;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #fetchStoreInventory Error: ${e}`);
  }
};

export const getPrettyHours = (combinedDayHours) => {
  try {
    return combinedDayHours.map((combinedDay) => {
      const { days, hours } = combinedDay;

      const startDay = days[0].charAt(0).toUpperCase() + days[0].substr(1);
      const endDay =
        days[days.length - 1].charAt(0).toUpperCase() +
        days[days.length - 1].substr(1);

      if (days.length === 1)
        return `${
          days[0].charAt(0).toUpperCase() + days[0].substr(1)
        }: ${hours}`;
      return `${startDay} - ${endDay}: ${hours}`;
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #getPrettyHours Error: ${e}`);
  }
};

export const getFormattedSchedule = (store) => {
  try {
    const tempObj = {};
    const hoursData = getStoreHours(store);

    for (const key in hoursData) {
      if (hoursData.hasOwnProperty(key)) {
        const day = key.substring(0, key.indexOf("_hours"));

        if (!tempObj[day]) {
          tempObj[day] = {};
        }

        key.includes("open")
          ? (tempObj[day].open = hoursData[key])
          : (tempObj[day].close = hoursData[key]);
      }
    }

    const hoursObjects = getCombinedHoursByDay(tempObj);
    return getPrettyHours(getCombinedDays(hoursObjects));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #getFormattedSchedule Error: ${e}`);
  }
};

const renameObjectKey = (obj, key, newKey) => {
  try {
    if (includes(keys(obj), key)) {
      obj[newKey] = clone(obj[key], true);
      delete obj[key];
    }

    return obj;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #renameObjectKey Error: ${e}`);
  }
};

export const getFormattedRawStores = (stores) => {
  try {
    return stores.reduce((result, o) => {
      let store = o;
      const schedule = getFormattedSchedule(o);
      const formattedPhone = getFormattedPhone(o.phone);
      const address1 = store.address;
      store = renameObjectKey(store, "MMS_Id", "id");

      store = {
        ...store,
        address1,
        schedule,
        formattedPhone,
      };

      return [...result, store];
    }, []);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #getFormattedRawStores Error: ${e}`);
  }
};

export const fetchAllStoreLocations = async () => {
  try {
    const response = await util.api({ url: getStoresLocationUrl() });
    return { stores: getFormattedRawStores(response.data.stores) };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #fetchAllStoreLocations Error: ${e}`);
  }
};

export const getStoresByCountry = (stores, country) => {
  try {
    return stores.filter((store) => store.country === country);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`Stores service: #getStoresByCountry Error: ${e}`);
  }
};

export const getStoreWithUpdatedInventory = (store, updatedInventory) => {
  try {
    const updatedStore = updatedInventory.find(
      (inventory) => inventory.storeId === store.id
    );

    if (!updatedStore) return store;

    return {
      ...store,
      inventory: updatedStore.inventory[0],
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`#getStoreWithUpdatedInventory Error: ${e}`);
  }
};

export const getOnHandStoreInventory = (
  stores,
  updatedInventory,
  isBopisEnabled = true
) => {
  try {
    const onHandInvStoreList = [];
    const updatedStores = stores.map((store) =>
      getStoreWithUpdatedInventory(store, updatedInventory)
    );

    const bopisInvStoreList = updatedStores.filter((store) => {
      const isOnHandInventoryAvailable = !!Number(store.inventory?.quantity);
      const isBopisInventoryAvailable = !!Number(
        store.inventory?.bopisQuantity
      );
      if (!isOnHandInventoryAvailable) return false;

      if (!isBopisEnabled || !isBopisInventoryAvailable) {
        onHandInvStoreList.push(store);
        return false;
      }

      return true;
    });

    return {
      bopisInvStoreList,
      onHandInvStoreList,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`#getOnHandStoreInventory Error: ${e}`);
  }
};

export const getOrderedBySelectedStores = (
  selectedStore,
  stores,
  storesWithInventory
) => {
  try {
    return stores.reduce((acc, store) => {
      const updatedStore = getStoreWithUpdatedInventory(
        store,
        storesWithInventory
      );

      if (store.id === selectedStore.id) {
        return [updatedStore, ...acc];
      }

      return [...acc, updatedStore];
    }, []);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`#getOrderedBopisStores Error: ${e}`);
  }
};

export const getStoresWithBopisInventory = (stores, updatedInventory) => {
  try {
    const inventory = stores.map((store) =>
      getStoreWithUpdatedInventory(store, updatedInventory)
    );

    return inventory.filter(
      (store) => !!Number(store.inventory?.bopisQuantity)
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`#getStoresWithAvailableStock Error: ${e}`);
  }
};

export const getStoresByProximity = (
  stores,
  maxDistance,
  lat,
  lng,
  userLatitude,
  userLongitude,
  country
) => {
  try {
    // TODO: do lat and long ever actually come through?
    const latitude = lat || userLatitude;
    const longitude = lng || userLongitude;

    // add proximity data to the store object
    // Only for stores that has log and lat.
    const storeList = stores
      .filter((store) => store?.longitude && store?.latitude)
      .map((store) => {
        let unitOfDistance = country === "US" ? "mile" : "kilometer";
        const distanceToStore = getDistance(
          { latitude, longitude },
          { latitude: store.latitude, longitude: store.longitude },
          unitOfDistance
        );

        if (unitOfDistance === "kilometer") {
          unitOfDistance = "km";
        } else {
          unitOfDistance = `${unitOfDistance}${
            distanceToStore <= 1 ? "" : "s"
          }`;
        }

        const secondaryText = `${distanceToStore} ${unitOfDistance}`;
        return {
          ...store,
          secondaryText,
          distanceToStore,
        };
      });

    // filter store stores to those within distance set by Distance dropdown
    const visibleStores = storeList.filter(
      (x) => x.distanceToStore < maxDistance
    );
    // order store stores in order of proximity
    return orderBy(visibleStores, ["distanceToStore", "asc"]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.trace(`#getStoresByProximity Error: ${e}`);
  }
};

/**
 * Takes the `hours` array from each `storeLocatorList` store object
 * and formats it to display in UI
 *
 * @see https://json-ld.org/
 * @param {Array} hoursByDay
 * @example
 * take an array of objects such as
 * [{
    "openHour": "11:00",
    "name": "friday",
    "closeHour": "19:00",
    "__typename": "StoreHoursDay"
  }]
 * @returns {Array} an array of store hours grouped by hour range
 * @example
 * returns an array formatted as
 * [ "sunday: 11:00-18:00", "monday - saturday: 10:00-21:00" ]
 */
const formatStoreHours = (hoursByDay) => {
  const hoursRangeByDay = Object.assign(
    {},
    ...hoursByDay.map((day) => ({
      [day.name]: {
        open: day.openHour,
        close: day.closeHour,
      },
    }))
  );
  const combinedDaysByDay = getCombinedHoursByDay(hoursRangeByDay);
  const combinedDays = getCombinedDays(combinedDaysByDay);
  return getPrettyHours(combinedDays);
};

const getStorePostalCode = (address) => {
  return address ? address.split(",").slice(-1)[0].trim() : "";
};

/**
 * Takes the `storeLocatorList` response and formats each store object
 *
 * @see https://json-ld.org/
 * @param {Array} stores the stores response from the storeLocatorList query
 * @returns {Array} an array with formatted stores data
 */
export const formatStores = (stores) => {
  return stores?.reduce((accumulator, store) => {
    const isStoreBopis = store.storeLocatorAmenities.buyOnlinePickupStore;

    if (isStoreBopis) {
      accumulator.push({
        ...store,
        unformattedHours: store.hours,
        specialHours: store.specialHours,
        hours: store.hours?.length > 0 ? formatStoreHours(store.hours) : [],
        formattedPhone: store.phone ? getFormattedPhone(store.phone) : "",
        postalcode: getStorePostalCode(store.fullAddress),
      });
    }
    return accumulator;
  }, []);
};
